import classnames from 'classnames';
import type { IntlKey } from 'locales';
import { ChangeEvent, FC, KeyboardEvent, ReactNode, useCallback } from 'react';
import { useIntl } from 'react-intl';

interface SearchInputProps {
  children: ReactNode;

  disabled?: boolean;
  size?: 'sm' | 'lg' | 'xl';
  autofocus?: boolean;
  placeholder?: IntlKey;
  searching: boolean;
  defaultValue?: string;

  e2e?: string;
  onSearchChanged: (value: string) => void;
  onUserValidated: () => void;
  onMoveCursorUp: () => void;

  onMoveCursorDown: () => void;
  onFocus?: (hasFocus: boolean) => void;
}

export const SearchInput: FC<SearchInputProps> = ({
  onSearchChanged,
  size,
  autofocus,
  onUserValidated,
  onMoveCursorDown,
  onMoveCursorUp,
  searching,
  children,
  onFocus,
  disabled = false,
  defaultValue,
  placeholder = 'client.search.placeholder',
  e2e = 'client-selector-search-input',
}) => {
  const { formatMessage } = useIntl();

  const onChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => onSearchChanged(e.target.value),
    [onSearchChanged],
  );

  const onKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      switch (e.key) {
        case 'Enter':
          onUserValidated();
          break;
        case 'ArrowDown':
          onMoveCursorDown();
          break;
        case 'ArrowUp':
          onMoveCursorUp();
          break;
        default:
          break;
      }
    },
    [onUserValidated, onMoveCursorDown, onMoveCursorUp],
  );

  return (
    <div className={classnames('input-group', size ? `input-group-${size}` : '')}>
      <div className="input-icon-start">
        {searching ? <div className="spinner spinner-sm" /> : <em className="icon icon-md">search</em>}
      </div>
      <input
        autoFocus={autofocus}
        defaultValue={defaultValue}
        type="search"
        className="form-control"
        placeholder={formatMessage({ id: placeholder })}
        onChange={onChange}
        onKeyDown={onKeyDown}
        data-e2e={e2e}
        onFocus={() => onFocus?.(true)}
        onBlur={() => onFocus?.(false)}
        disabled={disabled}
      />
      {children}
    </div>
  );
};

SearchInput.displayName = 'ClientSearchInput';
